import { useCallback, useEffect, useState, type RefObject } from 'react'

export interface WindowOptions {
  count: number
  /** Fixed row height in px. Rows in a virtual list must not vary. */
  rowHeight: number
  /** Extra rows rendered above and below the visible range. */
  overscan?: number
  /** Below this count everything renders (no virtualisation). */
  threshold?: number
}

export interface WindowRange {
  start: number
  end: number
  /** Spacer heights so the scrollbar reflects the full list. */
  before: number
  after: number
  isVirtual: boolean
  /** Scroll so row `index` is visible (keyboard navigation). */
  scrollToIndex: (index: number) => void
}

/**
 * Minimal fixed-height windowing for a scrolling list element (Combobox
 * options, TransferList). No dependency: the lists here have one row shape.
 */
export function useWindow(ref: RefObject<HTMLElement | null>, options: WindowOptions): WindowRange {
  const { count, rowHeight, overscan = 8, threshold = 200 } = options
  const isVirtual = count > threshold
  const [scrollTop, setScrollTop] = useState(0)
  const [viewport, setViewport] = useState(rowHeight * 12)

  useEffect(() => {
    const el = ref.current
    if (!el || !isVirtual) return
    const onScroll = () => {
      setScrollTop(el.scrollTop)
    }
    const measure = () => {
      setViewport(el.clientHeight || rowHeight * 12)
    }
    measure()
    el.addEventListener('scroll', onScroll, { passive: true })
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    ro?.observe(el)
    return () => {
      el.removeEventListener('scroll', onScroll)
      ro?.disconnect()
    }
  }, [ref, isVirtual, rowHeight])

  const scrollToIndex = useCallback(
    (index: number) => {
      const el = ref.current
      if (!el) return
      // Rendered rows scroll by where they really are: index × rowHeight
      // drifts from the real layout further down a list.
      const row = el.querySelector<HTMLElement>(`[data-index="${String(index)}"]`)
      if (row && typeof row.scrollIntoView === 'function') {
        row.scrollIntoView({ block: 'nearest' })
        return
      }
      if (!isVirtual) return
      // Windowed and not rendered yet: the fixed row height is exact there.
      const top = index * rowHeight
      const bottom = top + rowHeight
      if (top < el.scrollTop) el.scrollTop = top
      else if (bottom > el.scrollTop + el.clientHeight) el.scrollTop = bottom - el.clientHeight
    },
    [ref, rowHeight, isVirtual]
  )

  if (!isVirtual) return { start: 0, end: count, before: 0, after: 0, isVirtual, scrollToIndex }

  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
  const end = Math.min(count, Math.ceil((scrollTop + viewport) / rowHeight) + overscan)
  return {
    start,
    end,
    before: start * rowHeight,
    after: (count - end) * rowHeight,
    isVirtual,
    scrollToIndex
  }
}
